import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {boolean} */
  #rewardsFetched = false;

  connectedCallback() {
    super.connectedCallback?.();
    this.#observeDisclosureOpen();
    this.#autoApplyReferralDiscount();
  }

  #autoApplyReferralDiscount() {
    const email = this.dataset.customerEmail;
    if (!email) return;

    fetch(`/apps/loyalty/auto-discount?email=${encodeURIComponent(email)}`, { credentials: 'include' })
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (!data?.code) return;
        // Check if this code is already applied
        const existing = this.#existingDiscounts();
        if (existing.includes(data.code)) return;
        this.#applyRewardCode(data.code);
      })
      .catch(() => {});
  }

  #observeDisclosureOpen() {
    if (!this.dataset.customerEmail) return;

    const disclosure = this.closest('disclosure-custom');
    if (!disclosure) return;

    const trigger = disclosure.querySelector('[aria-expanded]');
    if (!trigger) return;

    // If already expanded (e.g. discount codes applied), fetch immediately
    if (trigger.getAttribute('aria-expanded') === 'true') {
      this.#fetchUnusedRewards();
      return;
    }

    // Otherwise wait for the panel to open
    const observer = new MutationObserver(() => {
      if (trigger.getAttribute('aria-expanded') === 'true') {
        observer.disconnect();
        this.#fetchUnusedRewards();
      }
    });
    observer.observe(trigger, { attributes: true, attributeFilter: ['aria-expanded'] });
  }

  #fetchUnusedRewards() {
    const email = this.dataset.customerEmail;
    if (!email || this.#rewardsFetched) return;

    const loadingEl = this.refs.cartDiscountRewardsLoading;
    const listEl = this.refs.cartDiscountRewardsList;
    if (!loadingEl || !listEl) return;

    this.#rewardsFetched = true;

    fetch(`/apps/loyalty/unused-rewards?email=${encodeURIComponent(email)}`, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        loadingEl.style.display = 'none';

        if (!data.ok || !Array.isArray(data.unused_rewards) || data.unused_rewards.length === 0) {
          listEl.innerHTML = '<li class="cart-discount__rewards-empty"><small class="cart-primary-typography">No unused rewards available.</small></li>';
          return;
        }

        listEl.innerHTML = '';
        for (const reward of data.unused_rewards) {
          const li = document.createElement('li');
          li.className = 'cart-discount__reward-item';
          li.innerHTML = `
            <span class="cart-discount__reward-name">${reward.name}</span>
            <button type="button" class="cart-discount__reward-apply" data-reward-code="${reward.code}">Apply</button>
          `;
          li.querySelector('.cart-discount__reward-apply')?.addEventListener('click', (e) => {
            e.preventDefault();
            this.#applyRewardCode(reward.code);
          });
          listEl.appendChild(li);
        }
      })
      .catch(() => {
        loadingEl.style.display = 'none';
        listEl.innerHTML = '<li class="cart-discount__rewards-empty"><small class="cart-primary-typography">Could not load rewards.</small></li>';
      });
  }

  #applyRewardCode(code) {
    const input = this.querySelector('input[name="discount"]');
    if (!(input instanceof HTMLInputElement)) return;

    input.value = code;
    const form = input.closest('form');
    if (form) form.requestSubmit();
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
